import { useCallback, useState } from 'react';
import { useJourneyAdmin } from './useJourneyAdmin';
import { useProspectingAvailability } from './useProspectingAvailability';
import { useDeleteLeadList, useRemoveMembership } from './hooks';
import { JourneyOperationDialog } from './JourneyOperationDialog';
import type { JourneyIntent } from './journeyOperations';
import { copyMessageLabel, DRAFT_COPY_GROUP } from './copyMessages';
import {
  Badge,
  Button,
  Drawer,
  EmptyState,
  InlineError,
  Input,
  SaveHint,
  Select,
  Skeleton,
  useAutoSave,
} from '../../components/ui';
import { KeyValueRow } from './KeyValueRows';
import { ImportDrawer } from './LeadDrawers';
import { LeadPanel } from './LeadPanel';
import {
  useAddLeadsToList,
  useAllMemberships,
  useCopySets,
  useCreateLeadList,
  useLeadLists,
  useMemberships,
  useRenameLeadList,
  useUpdateLead,
  useUpdateMembership,
} from './hooks';
import { newLeadStage, repliedStage } from './stageKeys';
import { dayLabel, todayIso } from './trackerFormat';
import { holdLabel, isLeadHeld } from './leadHold';
import {
  actionDateOrToday,
  isBackdated,
  markRepliedChanges,
  markSentChanges,
} from './outreachActionDate';
import {
  SEQ_LABELS,
  SEQ_TONES,
  copySetIndex,
  dueDateChanges,
  dueDateRefusal,
  gridTouches,
  handSetDueOn,
  membershipTouches,
  setOfMembership,
  leadName,
  listIndexStats,
  salesErrorMessage,
  searchLeads,
  touchDueOn,
  touchOf,
} from './types';
import type { Lead, LeadList, LeadStage, Membership, SequenceStatus, Touch } from './types';

/**
 * The "due" line under an unticked touch box. Click it to set the day by hand
 * (Ced, kyu-20260929-editable-touch-dates). Save writes that one touch's
 * touchN_due_on; "Back to automatic" writes null. No trigger reads the column,
 * so nothing is logged and no stage moves.
 */
function TouchDueDate({
  membership,
  touch,
  dueOn,
  today,
  who,
  saving,
  onSave,
}: {
  membership: Membership;
  touch: Exclude<Touch, 1>;
  dueOn: string;
  today: string;
  who: string;
  saving: boolean;
  onSave: (on: string | null, done: () => void) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [pickedDue, setPickedDue] = useState(dueOn);
  const handSet = handSetDueOn(membership, touch) !== null;
  const refusal = pickedDue === '' ? '' : (dueDateRefusal(membership, touch, pickedDue) ?? '');
  const tone = `tracker-touch-date${dueOn < today ? ' tracker-touch-date--overdue' : ''}`;

  if (!editing) {
    return (
      <p className={tone}>
        <button
          type="button"
          className="tracker-touch-due"
          aria-label={`Change the Touch ${touch} due date for ${who}, due ${dueOn}`}
          onClick={() => {
            setPickedDue(dueOn);
            setEditing(true);
          }}
        >
          due {dueOn}
        </button>
        {handSet && (
          <span className="tracker-touch-set" title="Set by hand. Back to automatic clears it.">
            {' '}
            set
          </span>
        )}
      </p>
    );
  }
  const close = () => setEditing(false);
  return (
    <div className="tracker-touch-due-editor">
      <input
        type="date"
        className="ui-input"
        aria-label={`Touch ${touch} due date for ${who}`}
        value={pickedDue}
        onChange={(event) => setPickedDue(event.target.value)}
      />
      {refusal !== '' && (
        <p className="tracker-touch-date tracker-touch-date--overdue">{refusal}</p>
      )}
      <Button
        size="sm"
        disabled={saving || pickedDue === '' || refusal !== ''}
        onClick={() => onSave(pickedDue, close)}
      >
        Save
      </Button>
      {handSet && (
        <Button variant="ghost" size="sm" disabled={saving} onClick={() => onSave(null, close)}>
          Back to automatic
        </Button>
      )}
      <Button variant="ghost" size="sm" onClick={close}>
        Cancel
      </Button>
    </div>
  );
}

function ListFormDrawer({
  list,
  onClose,
}: {
  list: LeadList | null; // null = create
  onClose: () => void;
}) {
  const create = useCreateLeadList();
  const rename = useRenameLeadList();
  const [error, setError] = useState('');
  const [name, setName] = useState(list?.name ?? '');
  const [type, setType] = useState(list?.type ?? 'Outbound');

  const pending = create.isPending;

  /**
   * Renaming an existing list saves on blur; creating one cannot, because
   * there is no row to write into until the button below makes it.
   */
  const renameSave = useAutoSave<string>(
    useCallback(
      (next: string) => {
        if (list === null) {
          return Promise.resolve();
        }
        if (next.trim() === '') {
          return Promise.reject(new Error('Give the list a name first.'));
        }
        setError('');
        return new Promise((resolve, reject) => {
          rename.mutate(
            { listId: list.id, name: next.trim() },
            {
              onSuccess: resolve,
              onError: (cause) => {
                setError(salesErrorMessage(cause.message));
                reject(cause);
              },
            },
          );
        });
      },
      [list, rename],
    ),
  );

  function createList() {
    if (name.trim() === '') {
      setError('Give the list a name first.');
      return;
    }
    setError('');
    create.mutate(
      { name, type },
      { onSuccess: onClose, onError: (cause: Error) => setError(salesErrorMessage(cause.message)) },
    );
  }

  return (
    <Drawer
      open
      title={list === null ? 'New list' : 'Rename list'}
      onClose={onClose}
      footer={
        list === null ? (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={createList} disabled={pending}>
              {pending ? 'Saving…' : 'Create list'}
            </Button>
          </>
        ) : undefined
      }
    >
      {error !== '' && <InlineError>{error}</InlineError>}
      {list === null ? (
        <>
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <Select
            label="Type"
            value={type}
            options={[
              { value: 'Outbound', label: 'Outbound' },
              { value: 'Nurture', label: 'Nurture' },
            ]}
            onChange={(e) => setType(e.target.value)}
          />
        </>
      ) : (
        <div className="kv-list">
          <KeyValueRow
            label="Name"
            hint={<SaveHint state={renameSave.state} error={renameSave.error} />}
          >
            <input
              className="kv-input"
              type="text"
              aria-label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              onBlur={() => renameSave.save(name)}
            />
          </KeyValueRow>
        </div>
      )}
    </Drawer>
  );
}

function AddLeadsDrawer({
  listId,
  leads,
  memberLeadIds,
  onClose,
}: {
  listId: string;
  leads: Lead[];
  memberLeadIds: Set<string>;
  onClose: () => void;
}) {
  const add = useAddLeadsToList();
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');

  const candidates = searchLeads(
    leads.filter((lead) => !memberLeadIds.has(lead.id)),
    query,
  ).slice(0, 50);

  function toggle(leadId: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      return next;
    });
  }

  function save() {
    setError('');
    const asked = picked.size;
    add.mutate(
      { listId, leadIds: [...picked] },
      {
        // `added` is what actually landed. Since 20261220000100 the database
        // silently drops a lead whose closing reason blocks contact, and the
        // candidate list here already excludes existing members — so any
        // shortfall is that guard and nothing else. Saying so beats closing the
        // drawer on a number the person never sees.
        onSuccess: (added) => {
          if (added < asked) {
            setError(
              `Added ${String(added)} of ${String(asked)}. The rest are marked Do not contact and were left out.`,
            );
            setPicked(new Set());
            return;
          }
          onClose();
        },
        onError: (cause) => setError(salesErrorMessage(cause.message)),
      },
    );
  }

  return (
    <Drawer
      open
      title="Add existing leads"
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={add.isPending || picked.size === 0}>
            {add.isPending ? 'Adding…' : `Add ${picked.size} lead${picked.size === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      {error !== '' && <InlineError>{error}</InlineError>}
      <Input
        label="Search leads"
        placeholder="Name, email, company…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {candidates.length === 0 ? (
        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          {query === '' ? 'Every lead is already in this list.' : 'No matches.'}
        </p>
      ) : (
        <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
          {candidates.map((lead) => (
            <label
              key={lead.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: 'var(--space-2) 0',
                borderBottom: '1px solid var(--border-subtle)',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={picked.has(lead.id)}
                onChange={() => toggle(lead.id)}
              />
              <span style={{ fontWeight: 'var(--font-weight-medium)' }}>{leadName(lead)}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
                {[lead.company_name, lead.email].filter(Boolean).join(' · ')}
              </span>
            </label>
          ))}
        </div>
      )}
    </Drawer>
  );
}

export function OutreachView({ leads, stages }: { leads: Lead[]; stages: LeadStage[] }) {
  const isAdmin = useJourneyAdmin();
  const { operations_enabled, message_enabled } = useProspectingAvailability();
  const removeFromList = useRemoveMembership();
  const deleteList = useDeleteLeadList();
  const [cleanup, setCleanup] = useState<JourneyIntent[] | null>(null);
  const listsQuery = useLeadLists();
  const copySetsQuery = useCopySets();
  const [openListId, setOpenListId] = useState<string | null>(null);
  /**
   * The lead panel, opened from a name in the list.
   *
   * Held HERE rather than lifted to TrackerPage on purpose. The page already
   * owns a LeadPanel for the Leads tab, but only one tab renders at a time, so
   * two instances can never be on screen together — and keeping this one local
   * means the Lists tab gained the panel without touching the page at all,
   * which mattered while another thread held that file.
   */
  const [openLeadId, setOpenLeadId] = useState<string | null>(null);
  const membershipsQuery = useMemberships(openListId);
  const allMembershipsQuery = useAllMemberships();
  const update = useUpdateMembership(openListId);
  const updateLead = useUpdateLead();
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [listDialog, setListDialog] = useState<'closed' | 'new' | 'rename'>('closed');
  const [addingLeads, setAddingLeads] = useState(false);
  const [importingHere, setImportingHere] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmingBulkRemove, setConfirmingBulkRemove] = useState(false);
  /**
   * The day every Mark sent and Replied on this screen records.
   *
   * Defaults to today, so working the list live is unchanged. Held for the
   * session rather than asked per row, because the case it exists for is
   * catching up on twenty leads at once — see outreachActionDate.ts.
   */
  const [actionDate, setActionDate] = useState(() => todayIso());

  const lists = listsQuery.data ?? [];
  const indexStats = listIndexStats(allMembershipsQuery.data ?? []);
  const copySets = copySetsQuery.data ?? [];
  const openList = lists.find((l) => l.id === openListId) ?? null;
  const openLead = leads.find((l) => l.id === openLeadId) ?? null;
  const leadById = new Map(leads.map((l) => [l.id, l]));
  const today = todayIso();

  function report(cause: Error) {
    setError(salesErrorMessage(cause.message));
  }

  if (openList === null) {
    if (listsQuery.isPending) return <Skeleton lines={3} label="Loading lists" />;
    return (
      <>
        <div
          style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-4)' }}
        >
          <Button variant="secondary" size="sm" onClick={() => setListDialog('new')}>
            New list
          </Button>
        </div>
        {lists.length === 0 ? (
          <EmptyState title="No lists yet" description="Create the first outreach list." />
        ) : (
          <div className="tracker-table-scroll">
            <table className="tracker-table">
              <thead>
                <tr>
                  <th>List</th>
                  <th>Type</th>
                  <th>Leads</th>
                  <th>Reply rate</th>
                  <th>Last touch</th>
                </tr>
              </thead>
              <tbody>
                {lists.map((list) => {
                  const stat = indexStats.get(list.id);
                  return (
                    <tr key={list.id}>
                      <td>
                        <button
                          type="button"
                          className="tracker-list-link"
                          onClick={() => {
                            setOpenListId(list.id);
                            setSelectedIds(new Set());
                            setConfirmingBulkRemove(false);
                          }}
                        >
                          {list.name}
                        </button>
                      </td>
                      <td>
                        <Badge tone="neutral">{list.type === '' ? 'List' : list.type}</Badge>
                      </td>
                      <td>{stat === undefined ? '—' : stat.leads}</td>
                      <td>{stat === undefined ? '—' : stat.replyLabel}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>
                        {stat?.lastTouch ?? 'no touch yet'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {listDialog === 'new' && (
          <ListFormDrawer list={null} onClose={() => setListDialog('closed')} />
        )}
      </>
    );
  }

  const memberships = membershipsQuery.data ?? [];
  const memberLeadIds = new Set(memberships.map((m) => m.lead_id));
  const visibleMemberships =
    query.trim() === ''
      ? memberships
      : memberships.filter((m) => {
          const lead = leadById.get(m.lead_id);
          return lead !== undefined && searchLeads([lead], query).length > 0;
        });
  // Where a reply moves a lead. Kyu: "reply received, inbound LinkedIn
  // message, or call booked" → Engaged. One name for it, so the Lists grid
  // and the timeline cannot promote a lead to two different places.
  const talksStage = repliedStage(stages);

  // Bulk selection (the old tracker's check-many/check-all column).
  // The grid's touch columns. Drop 139: a campaign can now be up to eight
  // emails long, so the column count follows the longest campaign on screen
  // instead of always being four.
  const setsById = copySetIndex(copySets);
  const shownTouches = gridTouches(visibleMemberships, setsById);

  const selected = visibleMemberships.filter((m) => selectedIds.has(m.id));
  const allVisibleSelected =
    visibleMemberships.length > 0 && selected.length === visibleMemberships.length;

  function toggleAllVisible() {
    setSelectedIds(allVisibleSelected ? new Set() : new Set(visibleMemberships.map((m) => m.id)));
  }

  function toggleSelected(membershipId: string) {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(membershipId)) {
        next.delete(membershipId);
      } else {
        next.add(membershipId);
      }
      return next;
    });
  }

  function bulkMarkSent(touch: Touch) {
    setError('');
    // The chosen date, not todayIso(). See outreachActionDate.ts — every date
    // this grid wrote used to be the day it was typed, which is why a week of
    // back-tagging landed as a week of activity.
    const on = actionDateOrToday(actionDate, today);
    for (const membership of selected) {
      if (touchOf(membership, touch).sentOn === null) {
        update.mutate(
          { membershipId: membership.id, changes: markSentChanges(touch, on) },
          { onError: report },
        );
      }
    }
  }

  /**
   * Bulk Replied — new, and the reason the tagging was row by row.
   *
   * The grid could mark four touches in bulk and replies one at a time, so a
   * catch-up session meant one click per lead per reply. Same date, same
   * graduation rule as the single-row version; it just does not make you do
   * it two hundred times.
   */
  function bulkMarkReplied() {
    setError('');
    const on = actionDateOrToday(actionDate, today);
    for (const membership of selected) {
      // Already answered. Re-marking would overwrite a recorded reply date
      // with this one, which on a backdating session is how you lose the
      // right answer you entered ten minutes ago.
      if (membership.reply_touch !== null || membership.seq_status !== 'active') {
        continue;
      }
      applyReplied(membership, on);
    }
    setSelectedIds(new Set());
  }

  function bulkSetCopySet(copySetId: string) {
    setError('');
    for (const membership of selected) {
      update.mutate(
        { membershipId: membership.id, changes: { copy_set_id: copySetId || null } },
        { onError: report },
      );
    }
  }

  function bulkRemove() {
    if (!operations_enabled && cleanup !== null) return;
    setError('');
    if (!operations_enabled) {
      for (const membership of selected) removeFromList.mutate(membership.id, { onError: report });
      setSelectedIds(new Set());
      setConfirmingBulkRemove(false);
      return;
    }
    setCleanup(
      selected.map((membership) => ({
        kind: 'cleanup',
        table: 'lead_list_memberships',
        id: membership.id,
        label: `Remove ${leadById.has(membership.lead_id) ? leadName(leadById.get(membership.lead_id) as Lead) : membership.lead_id} from ${openList?.name ?? 'this list'}`,
      })),
    );
    setConfirmingBulkRemove(false);
  }
  const coldStage = newLeadStage(stages);

  /** One membership marked replied, on a given day. Shared by the row button and the bulk action. */
  function applyReplied(membership: Membership, on: string) {
    update.mutate(
      { membershipId: membership.id, changes: markRepliedChanges(membership, on) },
      { onError: report },
    );
    // Graduate the lead out of Cold, the way the old tracker did.
    const lead = leadById.get(membership.lead_id);
    if (lead !== undefined && talksStage !== null && lead.stage_id === coldStage?.id) {
      updateLead.mutate(
        { leadId: lead.id, changes: { stage_id: talksStage.id } },
        { onError: report },
      );
    }
  }

  function markReplied(membership: Membership) {
    setError('');
    applyReplied(membership, actionDateOrToday(actionDate, today));
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-2)',
        }}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setOpenListId(null);
            setQuery('');
            setConfirmingDelete(false);
            setSelectedIds(new Set());
            setConfirmingBulkRemove(false);
          }}
        >
          ← Lists
        </Button>
        <strong style={{ fontSize: 'var(--font-size-md)' }}>{openList.name}</strong>
        <Badge tone="neutral">{memberships.length} leads</Badge>
        <span style={{ flex: 1 }} />
        <Button variant="secondary" size="sm" onClick={() => setAddingLeads(true)}>
          Add existing leads
        </Button>
        <Button variant="secondary" size="sm" onClick={() => setImportingHere(true)}>
          Import into list
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setListDialog('rename')}>
          Rename
        </Button>
        {(!operations_enabled || isAdmin) &&
          (confirmingDelete ? (
            <>
              <Button
                variant="danger"
                size="sm"
                disabled={!operations_enabled && cleanup !== null}
                onClick={() => {
                  setError('');
                  if (!operations_enabled) {
                    deleteList.mutate(openList.id, {
                      onSuccess: () => setOpenListId(null),
                      onError: report,
                    });
                    return;
                  }
                  setCleanup([
                    {
                      kind: 'cleanup',
                      table: 'lead_lists',
                      id: openList.id,
                      label: `Remove list ${openList.name}`,
                    },
                  ]);
                }}
              >
                Really delete
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmingDelete(false)}>
                Keep it
              </Button>
            </>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setConfirmingDelete(true)}>
              Delete list
            </Button>
          ))}
      </div>
      <p
        style={{
          margin: '0 0 var(--space-4)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-muted)',
        }}
      >
        Cadence: each touch is due a set number of business days after the touch before it went out
        (Touch 2 +5, Touch 3 +5, Touch 4 +3), so a late send moves the later dates with it. Touch 5
        to Touch 8 spacing (+3 each) is a placeholder. Marking Replied stops remaining touches and
        graduates the lead.
      </p>
      <Input
        label="Search in this list"
        placeholder="Name, email, company…"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      {/* ── The date every action on this grid records ──────────────────────
          One control above the table rather than a date on each button, and
          rather than a dialog per row. The case it exists for is catching up
          on twenty leads at once: set it once, tag the batch, move on.

          It is ALWAYS VISIBLE, which is the other half of the design. A
          control that silently governs every write on a screen is how you get
          a live send stamped with last Tuesday — so when it is not today the
          grid says so in words and offers one click back. */}
      <div className="tracker-action-date">
        <Input
          label="Record these as happening on"
          type="date"
          value={actionDate}
          max={today}
          onChange={(event) => setActionDate(event.target.value)}
        />
        {isBackdated(actionDate, today) ? (
          <p className="tracker-field-note tracker-action-date-warning">
            Backdating — Mark sent and Replied will record {dayLabel(actionDate)}, not today.{' '}
            <button
              type="button"
              className="tracker-lead-link"
              onClick={() => {
                setActionDate(today);
              }}
            >
              Use today
            </button>
          </p>
        ) : (
          <p className="tracker-field-note">
            Set the real date if this happened earlier. The record is when it happened, not when you
            typed it.
          </p>
        )}
      </div>

      {error !== '' && <InlineError>{error}</InlineError>}
      {message_enabled && copySets.some((set) => set.review_state === 'draft') && (
        <p className="tracker-field-note">
          Draft messages are visible for reference but cannot be assigned to campaigns. Only Kyu can
          approve them in Copy Library.
        </p>
      )}
      {selected.length > 0 && (
        <div className="tracker-bulk-bar">
          <strong>{selected.length} selected</strong>
          <span className="tracker-bulk-bar-label">Mark sent:</span>
          {shownTouches.map((touch) => (
            <Button key={touch} variant="secondary" size="sm" onClick={() => bulkMarkSent(touch)}>
              T{touch}
            </Button>
          ))}
          {/* New. Without it a catch-up session is one click per lead, which
              is what sent the team back through the threads one at a time. */}
          <Button variant="secondary" size="sm" onClick={bulkMarkReplied}>
            Replied
          </Button>
          <select
            className="ui-input ui-select tracker-grid-select"
            aria-label="Set copy set for selected"
            value=""
            onChange={(event) => {
              if (event.target.value !== '__noop__') {
                bulkSetCopySet(event.target.value === '__none__' ? '' : event.target.value);
              }
            }}
          >
            <option value="__noop__">Set copy set…</option>
            <option value="__none__">— none —</option>
            {copySets
              .filter((set) => !message_enabled || set.review_state !== 'draft')
              .map((set) => (
                <option
                  key={set.id}
                  value={set.id}
                  disabled={message_enabled && set.archived_at != null}
                >
                  {message_enabled ? copyMessageLabel(set) : set.name}
                </option>
              ))}
            {message_enabled && (
              <optgroup label={DRAFT_COPY_GROUP}>
                {copySets
                  .filter((set) => set.review_state === 'draft')
                  .map((set) => (
                    <option key={set.id} value={set.id} disabled>
                      {copyMessageLabel(set)}
                    </option>
                  ))}
              </optgroup>
            )}
          </select>
          <span style={{ flex: 1 }} />
          {confirmingBulkRemove ? (
            <>
              <Button variant="danger" size="sm" onClick={bulkRemove}>
                Remove {selected.length} from list
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmingBulkRemove(false)}>
                Keep
              </Button>
            </>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setConfirmingBulkRemove(true)}>
              Remove from list
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
            Clear
          </Button>
        </div>
      )}
      {membershipsQuery.isPending ? (
        <Skeleton lines={3} label="Loading list" />
      ) : (
        <div className="tracker-table-scroll">
          <table className="tracker-table">
            <thead>
              <tr>
                <th className="tracker-touch-cell">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    aria-label="Select all leads shown"
                    onChange={toggleAllVisible}
                  />
                </th>
                <th>Lead</th>
                <th>Copy set</th>
                {shownTouches.map((touch) => (
                  <th key={touch}>T{touch}</th>
                ))}
                <th>Sequence</th>
                <th>Reply</th>
                <th aria-label="Row actions" />
              </tr>
            </thead>
            <tbody>
              {visibleMemberships.map((membership) => {
                const lead = leadById.get(membership.lead_id) ?? null;
                const ownTouches = membershipTouches(
                  membership,
                  setOfMembership(membership, setsById),
                );
                return (
                  <tr key={membership.id}>
                    <td className="tracker-touch-cell">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(membership.id)}
                        aria-label={`Select ${lead === null ? membership.id : leadName(lead)}`}
                        onChange={() => toggleSelected(membership.id)}
                      />
                    </td>
                    <td style={{ fontWeight: 'var(--font-weight-medium)' }}>
                      {/* The name opens the lead, the way it does on the Leads
                          tab. Kyu: "No ability to open the Lead profile
                          (dialog box) if we click the lead's name from the
                          Lists tab." Working a list IS working leads — being
                          sent to another tab to look one up is the same
                          complaint as reading an address in the drawer. */}
                      {lead === null ? (
                        '—'
                      ) : (
                        <button
                          type="button"
                          className="tracker-lead-link"
                          title={`Open ${leadName(lead)}`}
                          onClick={() => setOpenLeadId(lead.id)}
                        >
                          {leadName(lead)}
                        </button>
                      )}
                      {/* Email under the name, the way the Leads tab has always
                          shown it in its own column. The person working this
                          list is writing the emails; making them open the
                          drawer to read an address is the whole complaint. */}
                      {lead !== null && lead.email !== '' && (
                        <p className="tracker-touch-email">{lead.email}</p>
                      )}
                      {lead !== null && lead.company_name !== '' && (
                        <p className="tracker-touch-date">{lead.company_name}</p>
                      )}
                      {lead !== null && isLeadHeld(lead, today) && (
                        <p className="tracker-touch-date">
                          <Badge tone="neutral">{holdLabel(lead)}</Badge>
                          {lead.email_hold_note.trim() === '' ? '' : ` ${lead.email_hold_note}`}
                        </p>
                      )}
                    </td>
                    <td>
                      <select
                        className="ui-input ui-select tracker-grid-select"
                        aria-label={`Copy set for ${lead === null ? membership.id : leadName(lead)}`}
                        value={membership.copy_set_id ?? ''}
                        onChange={(event) => {
                          setError('');
                          update.mutate(
                            {
                              membershipId: membership.id,
                              changes: { copy_set_id: event.target.value || null },
                            },
                            { onError: report },
                          );
                        }}
                      >
                        <option value="">—</option>
                        {copySets
                          .filter((set) => !message_enabled || set.review_state !== 'draft')
                          .map((set) => (
                            <option
                              key={set.id}
                              value={set.id}
                              disabled={message_enabled && set.archived_at != null}
                            >
                              {message_enabled ? copyMessageLabel(set) : set.name}
                            </option>
                          ))}
                        {message_enabled && (
                          <optgroup label={DRAFT_COPY_GROUP}>
                            {copySets
                              .filter((set) => set.review_state === 'draft')
                              .map((set) => (
                                <option key={set.id} value={set.id} disabled>
                                  {copyMessageLabel(set)}
                                </option>
                              ))}
                          </optgroup>
                        )}
                      </select>
                    </td>
                    {shownTouches.map((touch) => {
                      const state = touchOf(membership, touch);
                      const dueOn = touchDueOn(membership, touch, lead?.email_hold_until);
                      const held = lead !== null && isLeadHeld(lead, today);
                      const sent = state.sentOn !== null;
                      // The table has one column count, this row's campaign may
                      // be shorter. An empty box here would write a touch the
                      // campaign does not have, so there is no box to tick.
                      if (!ownTouches.includes(touch)) {
                        return (
                          <td key={touch} className="tracker-touch-cell">
                            <p
                              className="tracker-touch-date"
                              aria-label={`Touch ${touch} is not part of this campaign`}
                            >
                              &ndash;
                            </p>
                          </td>
                        );
                      }
                      return (
                        <td key={touch} className="tracker-touch-cell">
                          <input
                            type="checkbox"
                            checked={sent}
                            aria-label={`Touch ${touch} ${sent ? 'sent' : 'not sent'} for ${lead === null ? membership.id : leadName(lead)}`}
                            onChange={() => {
                              setError('');
                              update.mutate(
                                {
                                  membershipId: membership.id,
                                  // The row checkbox, which is how a touch is
                                  // marked one at a time. It wrote todayIso()
                                  // inline and was missed on the first pass of
                                  // this fix — the bulk buttons were changed
                                  // and this was not, which would have left
                                  // the commonest path still stamping today.
                                  // Found by the assertion, not by reading.
                                  changes: sent
                                    ? {
                                        [`touch${touch}_status`]: '',
                                        [`touch${touch}_sent_on`]: null,
                                      }
                                    : markSentChanges(touch, actionDateOrToday(actionDate, today)),
                                },
                                { onError: report },
                              );
                            }}
                          />
                          {sent ? (
                            <p className="tracker-touch-date">{state.sentOn}</p>
                          ) : held ? (
                            <p className="tracker-touch-date">
                              {dueOn === null ? 'on hold' : `on hold (due ${dueOn})`}
                            </p>
                          ) : (
                            dueOn !== null &&
                            touch !== 1 && (
                              <TouchDueDate
                                // Remount when the date moves under it, so an
                                // open picker never shows a stale default.
                                key={dueOn}
                                membership={membership}
                                touch={touch}
                                dueOn={dueOn}
                                today={today}
                                who={lead === null ? membership.id : leadName(lead)}
                                saving={update.isPending}
                                onSave={(on, done) => {
                                  setError('');
                                  update.mutate(
                                    {
                                      membershipId: membership.id,
                                      changes: dueDateChanges(touch, on),
                                    },
                                    { onSuccess: done, onError: report },
                                  );
                                }}
                              />
                            )
                          )}
                        </td>
                      );
                    })}
                    <td>
                      <select
                        className="ui-input ui-select tracker-grid-select"
                        aria-label={`Sequence for ${lead === null ? membership.id : leadName(lead)}`}
                        value={membership.seq_status}
                        onChange={(event) => {
                          setError('');
                          update.mutate(
                            {
                              membershipId: membership.id,
                              changes: { seq_status: event.target.value as SequenceStatus },
                            },
                            { onError: report },
                          );
                        }}
                      >
                        {(Object.keys(SEQ_LABELS) as SequenceStatus[]).map((status) => (
                          <option key={status} value={status}>
                            {SEQ_LABELS[status]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {membership.reply_touch !== null ? (
                        <Badge tone={SEQ_TONES[membership.seq_status]}>
                          T{membership.reply_touch}
                          {membership.reply_tag === '' ? '' : ` · ${membership.reply_tag}`}
                        </Badge>
                      ) : membership.seq_status === 'active' ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => markReplied(membership)}
                        >
                          Replied
                        </Button>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>—</span>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Remove ${lead === null ? membership.id : leadName(lead)} from list`}
                        disabled={!operations_enabled && cleanup !== null}
                        onClick={() => {
                          setError('');
                          if (!operations_enabled) {
                            removeFromList.mutate(membership.id, { onError: report });
                            return;
                          }
                          setCleanup([
                            {
                              kind: 'cleanup',
                              table: 'lead_list_memberships',
                              id: membership.id,
                              label: `Remove ${lead === null ? membership.lead_id : leadName(lead)} from ${openList.name}`,
                            },
                          ]);
                        }}
                      >
                        ✕
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {listDialog === 'rename' && (
        <ListFormDrawer list={openList} onClose={() => setListDialog('closed')} />
      )}
      {addingLeads && (
        <AddLeadsDrawer
          listId={openList.id}
          leads={leads}
          memberLeadIds={memberLeadIds}
          onClose={() => setAddingLeads(false)}
        />
      )}
      {importingHere && (
        <ImportDrawer
          open
          leads={leads}
          stages={stages}
          listId={openList.id}
          onClose={() => setImportingHere(false)}
        />
      )}

      {operations_enabled && cleanup !== null && (
        <JourneyOperationDialog
          intents={cleanup}
          onClose={() => setCleanup(null)}
          onSaved={() => {
            const removedList = cleanup.some(
              (intent) => intent.kind === 'cleanup' && intent.table === 'lead_lists',
            );
            setCleanup(null);
            setSelectedIds(new Set());
            setConfirmingDelete(false);
            if (removedList) setOpenListId(null);
          }}
        />
      )}

      {/* Keyed by lead id for the same reason TrackerPage keys its own: the
          panel's Contact fields hold draft state from the lead they first
          rendered with, so switching leads without a remount shows the
          previous lead's details under the new lead's name. Caught live on
          28 Aug and not repeated here. */}
      {openLead !== null && (
        <LeadPanel
          key={openLead.id}
          lead={openLead}
          stages={stages}
          allMemberships={allMembershipsQuery.data ?? []}
          onClose={() => setOpenLeadId(null)}
        />
      )}
    </div>
  );
}

// ── Companies view ────────────────────────────────────────────────────────────

/**
 * A borderless key-value row (redesign spec §Companies): the label sits in a
 * quiet left column and the value reads as plain text until you touch it.
 * Nothing here has a Save button — each row writes on blur, and the only
 * feedback is a hint that appears beside the label and fades.
 */
